import React, { useRef, useEffect } from "react";
import AsyncSelect from "react-select/async";
import Select, { components } from "react-select";

export const multiselectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    border: state.isFocused ? "1px solid #d35258" : "0.7px solid #E5E5E5",
    boxShadow: state.isFocused ? "0 0 0 1px #fad2d5" : "none",
    padding: "2px",
    fontWeight: "500",
    fontSize: "13px",
    color: "#212129",
    minHeight: "38px",
    "&:hover": {
      border: state.isFocused ? "1px solid #d35258" : "0.7px solid #fad2d5",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 4px",
    overflow: "visible",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#ffecec"
      : state.isFocused
        ? "#fff5f6"
        : "#fff",
    color: state.isSelected ? "#d35258" : "#212129",
    fontWeight: state.isSelected ? "600" : "500",
    padding: 10,
    fontSize: "13px",
    cursor: "pointer",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#F6F7F8",
    color: "#212129",
    borderRadius: "5px",
    padding: "2px 5px",
    margin: "2px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#212129",
    fontSize: "12px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#212129",
    cursor: "pointer",
    ":hover": {
      backgroundColor: "red",
      color: "white",
    },
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    scrollbarWidth: "thin",
    scrollbarColor: "#D4DDE7 transparent",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#D4DDE7",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#A0AEC0",
    },
  }),
};

const ValueContainerWithAutoScroll = ({ children, ...props }) => {
  const containerRef = useRef(null);
  const prevCount = useRef(0);
  const val = props.getValue();
  const count = Array.isArray(val) ? val.length : val ? 1 : 0;

  useEffect(() => {
    if (containerRef.current && count > 0) {
      if (count > prevCount.current) {
        const scrollToBottom = () => {
          if (containerRef.current) {
            containerRef.current.scrollTo({
              top: containerRef.current.scrollHeight,
              behavior: "smooth",
            });
          }
        };
        requestAnimationFrame(scrollToBottom);
        const timer = setTimeout(scrollToBottom, 50);
        return () => clearTimeout(timer);
      }
    }
    prevCount.current = count;
  }, [count]);

  return (
    <components.ValueContainer {...props}>
      <div
        ref={containerRef}
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          flex: 1,
          width: "100%",
          maxHeight: "80px",
          overflowY: "auto",
          padding: "1px",
          scrollbarWidth: "thin",
          scrollbarColor: "#D4DDE7 transparent",
        }}
      >
        {children}
      </div>
    </components.ValueContainer>
  );
};

const getMenuPortalTarget = (customTarget) => {
  if (customTarget !== undefined) return customTarget;
  return typeof document !== "undefined" ? document.body : null;
};

export const CommonAsyncSelect = ({
  styles,
  components: userComponents,
  menuPortalTarget,
  menuPosition = "fixed",
  menuPlacement = "auto",
  isClearable = false,
  ...props
}) => {
  return (
    <AsyncSelect
      styles={styles || multiselectCustomStyles}
      components={{
        ValueContainer: ValueContainerWithAutoScroll,
        ...userComponents,
      }}
      menuPortalTarget={getMenuPortalTarget(menuPortalTarget)}
      menuPosition={menuPosition}
      menuPlacement={menuPlacement}
      isClearable={isClearable}
      {...props}
    />
  );
};

export const CommonSelect = ({
  styles,
  components: userComponents,
  menuPortalTarget,
  menuPosition = "fixed",
  menuPlacement = "auto",
  isClearable = false,
  ...props
}) => {
  return (
    <Select
      styles={styles || multiselectCustomStyles}
      components={{
        ValueContainer: ValueContainerWithAutoScroll,
        ...userComponents,
      }}
      menuPortalTarget={getMenuPortalTarget(menuPortalTarget)}
      menuPosition={menuPosition}
      menuPlacement={menuPlacement}
      isClearable={isClearable}
      {...props}
    />
  );
};

export default CommonAsyncSelect;
